import { HttpErrorResponse } from "@angular/common/http";
import { formatPersonNameLastFirst } from "@app/shared/util/person-name";
import {
  PreHearingReadinessDisplay,
  ProjectionPreHearingReadiness,
  mapPreHearingReadiness,
} from "../readiness/pre-hearing-readiness.model";

/**
 * Raw proceeding-detail projection payload (GET /api/v1/scheduling/projections/proceedings/{id}).
 * The shape is treated as provisional until dependency 912 finalizes its API; all consumption goes
 * through {@link mapHearingDetailHeader} so the template never binds to the raw projection directly.
 */
export interface ProceedingDetailProjection {
  scheduledProceedingId: number;
  proceedingNumber: string;
  title?: string | null;
  proceedingTypeCode?: string | null;
  schedulingStatusCode: string;
  scheduledStartUtc?: string | null;
  scheduledEndUtc?: string | null;
  timeZoneId?: string | null;
  locationName?: string | null;
  roomName?: string | null;
  videoConferenceUrl?: string | null;
  /** Provisional pre-hearing readiness (dependency 912); absent renders a neutral unavailable state. */
  preHearingReadiness?: ProjectionPreHearingReadiness | null;
  /**
   * Adjudication linkage used to persist a hearing outcome through the decision (item 915) API and
   * to attempt the gated closeout transition. These are provisional on dependency 912 finalizing the
   * proceeding-detail projection; when absent the outcome surface stays read-only.
   */
  adjudicationCaseId?: number | null;
  adjudicationSubjectId?: number | null;
  adjudicationSubjectTypeCode?: string | null;
  workflowInstanceId?: number | null;
  workflowStepInstanceId?: number | null;
  /**
   * Typed panel/staff members (#PHMS-SCH-7). Replaces direct binding to the legacy raw
   * `panelAssignmentsJson` string; always consumed through {@link mapPanelStaff} so the template
   * never reads the untyped JSON. Absent/empty renders the "no panel assigned" state.
   */
  panelAssignments?: PanelStaffMemberProjection[] | null;
}

/** Typed panel/staff member as carried on the proceeding-detail projection (#PHMS-SCH-7). */
export interface PanelStaffMemberProjection {
  roleCode?: string | null;
  displayName?: string | null;
  identifier?: string | null;
}

/** Read-only Panel &amp; Staff row the flyout template binds to. */
export interface PanelStaffMember {
  roleCode: string;
  roleLabel: string;
  displayName: string;
  identifier: string | null;
}

/** Read-only view model the hearing-detail header template binds to. */
export interface HearingDetailHeader {
  scheduledProceedingId: number;
  hearingLabel: string;
  proceedingNumber: string;
  scheduledDateLabel: string;
  scheduledTimeLabel: string;
  locationLabel: string;
  statusCode: string;
  statusLabel: string;
  hasScheduledDate: boolean;
  hasScheduledTime: boolean;
  hasLocation: boolean;
  readiness: PreHearingReadinessDisplay;
  /** Adjudication linkage for the outcome surface; nulls keep the surface read-only (deferred on 912). */
  adjudicationCaseId: number | null;
  adjudicationSubjectId: number | null;
  adjudicationSubjectTypeCode: string | null;
  workflowInstanceId: number | null;
  workflowStepInstanceId: number | null;
}

export interface HearingDetailDisplayError {
  title: string;
  messages: string[];
  statusCode?: number;
}

/**
 * Calendar-centric appointment for a scheduled proceeding, mirroring ScheduledAppointmentDto
 * (GET /api/v1/scheduling/projections/proceedings/{id}/appointment). Drawn from the same calendar
 * projection the grid renders, so the flyout header never disagrees with the clicked chip; carries the
 * resolved {@link incarceratedPersonId} for the inline case snapshot.
 */
export interface ScheduledAppointment {
  scheduledProceedingId: number;
  incarceratedPersonId?: number | null;
  proceedingNumber: string;
  title: string;
  schedulingStatusCode: string;
  scheduledStartUtc?: string | null;
  scheduledEndUtc?: string | null;
  timeZoneId: string;
  locationName?: string | null;
  roomName?: string | null;
  videoConferenceUrl?: string | null;
  isScheduled: boolean;
}

/** Read-only calendar-appointment view model the flyout header binds to. */
export interface AppointmentDisplay {
  title: string;
  proceedingNumber: string;
  scheduledDateLabel: string;
  scheduledTimeLabel: string;
  locationLabel: string;
  locationNameLabel: string;
  roomNameLabel: string | null;
  formatLabel: string;
  statusCode: string;
  statusLabel: string;
  hasScheduledDate: boolean;
  incarceratedPersonId: number | null;
  scheduledStartUtc: string | null;
  scheduledEndUtc: string | null;
}

/**
 * Maps the calendar appointment payload to the flyout's calendar-first header view model. Isolated so
 * the template never binds the raw payload and the date/time/location/status formatting stays in one
 * place (shared with the hearing-detail header formatters).
 */
export function mapAppointmentDisplay(appointment: ScheduledAppointment): AppointmentDisplay {
  const title = (appointment.title ?? "").trim();
  const proceedingNumber = (appointment.proceedingNumber ?? "").trim();
  const startUtc = appointment.scheduledStartUtc ?? null;
  const endUtc = appointment.scheduledEndUtc ?? null;
  const isVideo = Boolean(appointment.videoConferenceUrl?.trim());
  const locationParts = [appointment.locationName?.trim(), appointment.roomName?.trim()].filter(
    (part): part is string => Boolean(part),
  );
  const locationLabel = locationParts.length > 0
    ? locationParts.join(" / ")
    : isVideo
      ? "Video"
      : MISSING_PLACEHOLDER;

  return {
    // The person name renders as "Last, First Middle" (#1436); the number/id fallbacks are
    // not names and pass through untouched.
    title: title ? formatPersonNameLastFirst(title) : proceedingNumber || `Hearing ${appointment.scheduledProceedingId}`,
    proceedingNumber: proceedingNumber || MISSING_PLACEHOLDER,
    scheduledDateLabel: formatDate(startUtc) ?? MISSING_PLACEHOLDER,
    scheduledTimeLabel: formatTimeRange(startUtc, endUtc),
    locationLabel,
    locationNameLabel: appointment.locationName?.trim() || (isVideo ? "Video" : MISSING_PLACEHOLDER),
    roomNameLabel: appointment.roomName?.trim() || null,
    formatLabel: isVideo ? "Video" : "In Person",
    statusCode: (appointment.schedulingStatusCode ?? "").trim(),
    statusLabel: formatStatus(appointment.schedulingStatusCode),
    hasScheduledDate: formatDate(startUtc) !== null,
    incarceratedPersonId: toPositiveOrNull(appointment.incarceratedPersonId),
    scheduledStartUtc: startUtc,
    scheduledEndUtc: endUtc,
  };
}

const MISSING_PLACEHOLDER = "—";

/**
 * Isolated mapping from the provisional proceeding-detail projection to the header view model.
 * A change in the 912 projection shape should only require editing this function, not the template.
 */
export function mapHearingDetailHeader(projection: ProceedingDetailProjection): HearingDetailHeader {
  const proceedingNumber = (projection.proceedingNumber ?? "").trim();
  const title = (projection.title ?? "").trim();
  const startUtc = projection.scheduledStartUtc ?? null;
  const endUtc = projection.scheduledEndUtc ?? null;

  return {
    scheduledProceedingId: projection.scheduledProceedingId,
    // The person name renders as "Last, First Middle" (#1436); the number/id fallbacks are
    // not names and pass through untouched.
    hearingLabel: title ? formatPersonNameLastFirst(title) : proceedingNumber || `Hearing ${projection.scheduledProceedingId}`,
    proceedingNumber: proceedingNumber || MISSING_PLACEHOLDER,
    scheduledDateLabel: formatDate(startUtc) ?? MISSING_PLACEHOLDER,
    scheduledTimeLabel: formatTimeRange(startUtc, endUtc),
    locationLabel: formatLocation(projection) ?? MISSING_PLACEHOLDER,
    statusCode: (projection.schedulingStatusCode ?? "").trim(),
    statusLabel: formatStatus(projection.schedulingStatusCode),
    hasScheduledDate: formatDate(startUtc) !== null,
    hasScheduledTime: Boolean(startUtc),
    hasLocation: formatLocation(projection) !== null,
    readiness: mapPreHearingReadiness(projection.preHearingReadiness),
    adjudicationCaseId: toPositiveOrNull(projection.adjudicationCaseId),
    adjudicationSubjectId: toPositiveOrNull(projection.adjudicationSubjectId),
    adjudicationSubjectTypeCode: (projection.adjudicationSubjectTypeCode ?? "").trim() || null,
    workflowInstanceId: toPositiveOrNull(projection.workflowInstanceId),
    workflowStepInstanceId: toPositiveOrNull(projection.workflowStepInstanceId),
  };
}

function toPositiveOrNull(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : null;
}

/**
 * Canonical Panel &amp; Staff role ordering and labels (#PHMS-SCH-7). The five recognised roles render
 * first in this order; any other roles follow in projected order. Keyed by normalized role code so
 * the provisional projection's role-code spelling variants resolve to a stable label/position.
 */
const PANEL_ROLE_PRESENTATION: Record<string, { order: number; label: string }> = {
  presiding: { order: 0, label: "Presiding" },
  presiding_officer: { order: 0, label: "Presiding" },
  deputy: { order: 1, label: "Deputy" },
  deputy_commissioner: { order: 1, label: "Deputy" },
  attorney: { order: 2, label: "Attorney" },
  deputy_attorney: { order: 2, label: "Attorney" },
  attorney_general: { order: 2, label: "Attorney" },
  hearing_officer: { order: 3, label: "Hearing Officer" },
  hearingofficer: { order: 3, label: "Hearing Officer" },
  clerk: { order: 4, label: "Clerk" },
  hearing_clerk: { order: 4, label: "Clerk" },
};

const OTHER_ROLE_ORDER = 5;

/**
 * Maps the typed projection panel list into ordered Panel &amp; Staff rows
 * (Presiding -> Deputy -> Attorney -> Hearing Officer -> Clerk), never binding the raw JSON. The
 * server already orders the list, but ordering is re-asserted here so the template is stable even if
 * the projection contract shifts (dependency 912). Entries without a display name are dropped.
 */
export function mapPanelStaff(projection: ProceedingDetailProjection): PanelStaffMember[] {
  const members = projection.panelAssignments ?? [];
  return members
    .map((member, index) => {
      const roleCode = (member.roleCode ?? "").trim();
      const presentation = PANEL_ROLE_PRESENTATION[roleCode.toLowerCase()];
      const displayName = (member.displayName ?? "").trim();
      const identifier = (member.identifier ?? "").trim();
      return {
        index,
        order: presentation?.order ?? OTHER_ROLE_ORDER,
        row: {
          roleCode,
          roleLabel: presentation?.label ?? formatStatus(roleCode),
          displayName,
          identifier: identifier.length > 0 ? identifier : null,
        } satisfies PanelStaffMember,
      };
    })
    .filter((entry) => entry.row.displayName.length > 0)
    .sort((a, b) => (a.order !== b.order ? a.order - b.order : a.index - b.index))
    .map((entry) => entry.row);
}

export function mapHearingDetailError(error: unknown): HearingDetailDisplayError {
  if (error instanceof HttpErrorResponse) {
    const statusCode = error.status;
    if (statusCode === 401) {
      return {
        title: "Sign-in required",
        messages: ["Your session is no longer authenticated. Sign in again to view this hearing."],
        statusCode,
      };
    }
    if (statusCode === 403) {
      return {
        title: "Access denied",
        messages: ["You do not have permission to view this hearing."],
        statusCode,
      };
    }
    if (statusCode === 404) {
      return {
        title: "Hearing not found",
        messages: ["No hearing detail is available for the requested proceeding."],
        statusCode,
      };
    }
    return {
      title: "Hearing detail unavailable",
      messages: ["The hearing detail could not be loaded. Try again or contact support if the problem persists."],
      statusCode,
    };
  }

  return {
    title: "Unexpected error",
    messages: ["The hearing detail could not be loaded. Try again or contact support if the problem persists."],
  };
}

export function formatStatus(statusCode: string | null | undefined): string {
  const normalized = (statusCode ?? "").trim();
  if (!normalized) {
    return "Unknown";
  }
  return normalized
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatLocation(projection: ProceedingDetailProjection): string | null {
  const parts = [projection.locationName?.trim(), projection.roomName?.trim()]
    .filter((part): part is string => Boolean(part));
  if (parts.length > 0) {
    return parts.join(" / ");
  }
  return projection.videoConferenceUrl?.trim() ? "Video" : null;
}

function formatDate(value: string | null): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : new Intl.DateTimeFormat(undefined, { dateStyle: "full" }).format(date);
}

function formatTimeRange(startUtc: string | null, endUtc: string | null): string {
  const start = formatTime(startUtc);
  if (!start) {
    return MISSING_PLACEHOLDER;
  }
  const end = formatTime(endUtc);
  return end ? `${start} – ${end}` : start;
}

function formatTime(value: string | null): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : new Intl.DateTimeFormat(undefined, { timeStyle: "short" }).format(date);
}
